import { ref } from 'vue';
import { VerseTableRow } from '@/interfaces/ExplorerTableRow';
import { ColumnKey, getColumnValue } from './explorerColumns';

export type SortKey = ColumnKey;

const createDirectKeyGetter = (key: SortKey): ((row: VerseTableRow) => number) | null => {
  switch (key) {
    case 'verse':
      return (row) => row.verse;
    case 'readings_count':
      return (row) => row.readings_count;
    case 'references_count':
      return (row) => row.references_count;
    case 'sources_count':
      return (row) => row.sources_count;
    case 'variants_count':
      return (row) => row.variants_count;
    case 'manuscripts_count':
      return (row) => row.manuscripts_count;
    case 'intertexts_count':
      return (row) => row.intertexts_count;
    default:
      return null;
  }
};

export function useExplorerSorting(): {
  sortKey: any;
  sortAsc: any;
  sortBy: (key: SortKey) => void;
  getRowValue: (row: VerseTableRow, columnKey: string) => number;
  sortRows: (rows: VerseTableRow[]) => VerseTableRow[];
  } {
  const sortKey = ref<SortKey>('sura');
  const sortAsc = ref(true);

  const sortBy = (key: SortKey) => {
    if (sortKey.value === key) {
      sortAsc.value = !sortAsc.value;
    } else {
      sortKey.value = key;
      sortAsc.value = true;
    }
  };

  const getRowValue = (row: VerseTableRow, columnKey: string): number => (
    getColumnValue(row, columnKey as ColumnKey)
  );

  const sortRows = (rows: VerseTableRow[]): VerseTableRow[] => {
    const sortedRows = [...rows];

    if (sortKey.value === 'sura') {
      sortedRows.sort((a, b) => {
        if (a.sura !== b.sura) {
          return sortAsc.value ? a.sura - b.sura : b.sura - a.sura;
        }
        return sortAsc.value ? a.verse - b.verse : b.verse - a.verse;
      });
    } else {
      const keyGetter = createDirectKeyGetter(sortKey.value);

      if (keyGetter) {
        sortedRows.sort((a, b) => {
          const aValue = keyGetter(a);
          const bValue = keyGetter(b);
          return sortAsc.value ? aValue - bValue : bValue - aValue;
        });
      } else {
        sortedRows.sort((a, b) => {
          const aValue = getColumnValue(a, sortKey.value);
          const bValue = getColumnValue(b, sortKey.value);
          return sortAsc.value ? aValue - bValue : bValue - aValue;
        });
      }
    }

    return sortedRows;
  };

  return {
    sortKey,
    sortAsc,
    sortBy,
    getRowValue,
    sortRows,
  };
}
